<template>
	<div>
		<el-dialog
			:visible.sync="visible"
			width="1400px"
			custom-class="action-detail-dialog"
			:before-close="handleClose"
		>
			<template slot="title">
				<div class="action-detail-header">
					<span class="action-detail-header__title">{{ componentTitle }}</span>
				</div>
			</template>

			<div v-if="detailLoading" class="action-detail-skeleton">
				<div class="skeleton-buttons">
					<el-skeleton :rows="0" animated>
						<template slot="template">
							<div class="skeleton-button-row">
								<el-skeleton-item variant="button" style="width: 120px; height: 32px" />
								<el-skeleton-item variant="button" style="width: 140px; height: 32px" />
								<el-skeleton-item variant="button" style="width: 120px; height: 32px" />
								<el-skeleton-item variant="button" style="width: 140px; height: 32px" />
							</div>
						</template>
					</el-skeleton>
				</div>

				<el-row :gutter="20">
					<el-col :span="skeletonMainPanelWidth">
						<el-skeleton :rows="0" animated>
							<template slot="template">
								<el-skeleton-item variant="rect" style="width: 100%; height: 900px" />
							</template>
						</el-skeleton>
					</el-col>
					<el-col v-if="skeletonRightPanelWidth > 0" :span="skeletonRightPanelWidth">
						<el-skeleton :rows="0" animated>
							<template slot="template">
								<el-skeleton-item variant="rect" style="width: 100%; height: 900px" />
							</template>
						</el-skeleton>
					</el-col>
				</el-row>
			</div>
			<action-detail
				v-else
				ref="actionDetail"
				:initial-action="selectedAction"
				:component-type="componentType"
				:on-action-changed="handleActionChanged"
			>
				<template v-if="queueActive" #extra-actions>
					<el-button
						size="mini"
						type="primary"
						:disabled="!queueHasNext || navigating"
						@click="navigateNext"
					>
						{{ $t('next') }}
					</el-button>
					<span class="queue-position-inline">{{ queuePosition }} / {{ queueLength }}</span>
				</template>
			</action-detail>
		</el-dialog>
	</div>
</template>
<script>
import ActionDetail from '@/views/_dialogs/ActionDetail';
import { mapGetters, mapState } from 'vuex';
export default {
	components: {
		ActionDetail
	},
	props: {
		visible: { required: true, type: Boolean },
		componentType: { type: String, default: '' }
	},
	data() {
		return {
			minLoadDelay: false,
			dataReady: false,
			navigating: false
		};
	},
	computed: {
		...mapGetters(['selectedParticipantStore', 'selectedAction', 'actionDetailLoading']),
		...mapGetters('worklist', {
			queueActive: 'actionQueueActive',
			queuePosition: 'actionQueuePosition',
			queueLength: 'actionQueueLength',
			queueCurrentItem: 'actionQueueCurrentItem',
			queueHasNext: 'actionQueueHasNext'
		}),
		...mapState({
			actionDetailRightPanelCollapsed: (state) => state.actions.actionDetailRightPanelCollapsed
		}),
		skeletonMainPanelWidth() {
			return this.actionDetailRightPanelCollapsed ? 24 : 17;
		},
		skeletonRightPanelWidth() {
			return this.actionDetailRightPanelCollapsed ? 0 : 7;
		},
		componentTitle() {
			if (!this.selectedAction) {
				return '';
			}

			const channel = this.selectedAction.channel;
			const messageCode = this.selectedAction.message_code;
			const isRetry = this.selectedAction.is_retry;

			const channelName = channel ? this.$translate.channel(channel) : '';
			const typeName = messageCode ? this.$translate.action_message_code(messageCode) : '';
			const retryIndicator = isRetry ? ' ↺' : '';

			if (channelName && typeName) {
				return `${typeName} / ${channelName}${retryIndicator}`;
			} else if (typeName) {
				return `${typeName}${retryIndicator}`;
			} else if (channelName) {
				return channelName;
			} else {
				return this.$tc('action', 1);
			}
		},
		detailLoading() {
			return !this.dataReady || this.minLoadDelay;
		}
	},
	watch: {
		visible: {
			immediate: true,
			async handler(isVisible) {
				const ref = this.$store.state.actions.selectedActionRef;
				if (isVisible && ref) {
					this.dataReady = false;
					this.minLoadDelay = true;

					setTimeout(() => {
						this.minLoadDelay = false;
					}, 300);

					await this.$store.dispatch('actions/fetchActionDetails', ref);
					this.dataReady = true;
				} else if (!isVisible) {
					this.dataReady = false;
				}
			}
		}
	},
	methods: {
		handleActionChanged(updatedAction) {
			this.$store.commit('actions/UPSERT_ACTION', updatedAction);
		},
		async navigateNext() {
			if (!this.queueHasNext || this.navigating) return;
			this.navigating = true;

			const nextIndex = this.$store.state.worklist.actionQueueIndex + 1;
			const nextItem = this.$store.state.worklist.actionQueue[nextIndex];
			if (!nextItem) {
				this.navigating = false;
				return;
			}

			this.$store.commit('worklist/SET_ACTION_QUEUE_INDEX', nextIndex);
			this.$store.commit('actions/SET_SELECTED_ACTION_REF', nextItem.ref);

			// Show loading state while fetching
			this.dataReady = false;
			this.minLoadDelay = true;
			setTimeout(() => { this.minLoadDelay = false; }, 300);

			try {
				const action = await this.$store.dispatch('actions/fetchActionDetails', nextItem.ref);
				if (action) {
					this.$store.commit('risolto/SET_SELECTED_RECIPIENT', action.recipient_by_reference);
					this.$store.commit('risolto/SET_COMPONENT_TYPE_STORE', action.channel);
				}
			} finally {
				this.dataReady = true;
				this.navigating = false;
			}
		},
		handleClose() {
			const isDirty = this.$refs.actionDetail?.isDirty;
			if (isDirty) {
				this.$confirm(this.$t('sureClose'), this.$t('warning'), {
					confirmButtonText: this.$t('close'),
					cancelButtonText: this.$t('cancel'),
					type: 'warning'
				})
					.then(() => {
						this.closeAndCleanup();
					})
					.catch(() => {
						// User cancelled — keep dialog open
					});
			} else {
				this.closeAndCleanup();
			}
		},
		closeAndCleanup() {
			this.$store.commit('risolto/SET_ACTION_DETAIL_DIALOG_VISIBLE', false);
			this.$store.commit('worklist/CLEAR_ACTION_QUEUE');
			this.$emit('onDialogClose', {});
		}
	}
};
</script>

<style>
.action-detail-dialog {
	max-width: 1400px;
	max-height: calc(100vh - 4vh);
	margin: 2vh auto !important;
}

.action-detail-dialog .el-dialog__header {
	padding-bottom: 0;
	border-bottom: none;
}

.action-detail-dialog .el-dialog__headerbtn {
	width: 40px;
	height: 40px;
	font-size: 18px;
}

.action-detail-dialog .el-dialog__close {
	font-size: 18px;
}

.action-detail-dialog .el-dialog__body {
	max-height: calc(100vh - 10vh);
	overflow-y: auto;
	padding-top: 0 !important;
}

.action-detail-dialog .action-detail-skeleton {
	padding: 10px;
}

.action-detail-dialog .skeleton-buttons {
	margin-bottom: 20px;
}

.action-detail-dialog .skeleton-button-row {
	display: flex;
	gap: 12px;
}

.action-detail-dialog .action-detail-header {
	display: flex;
	align-items: center;
	gap: 12px;
}

.action-detail-dialog .action-detail-header__title {
	font-size: 18px;
	font-weight: 600;
	color: #303133;
}

.action-detail-dialog .action-detail-header__position {
	font-size: 12px;
	color: #909399;
	font-weight: 400;
}

.queue-position-inline {
	font-size: 12px;
	color: #909399;
	white-space: nowrap;
	margin-left: 4px;
}
</style>
